import React from 'react';
import {
  Input, Row, Col, Switch, Divider, Typography,
} from 'antd';
import { Controller, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import DropZone, { FileType, MyFile } from '../../../components/DropZone';
import HeaderLabel from '../../../components/Form/HeaderLabel';
import AlumlyButton from '../../../components/AlumlyButton';
import TextEditor from '../../../components/TextEditor/TextEditor';
import { RouteNameEnum } from '../../../enums/routeName';
import { ServiceNameEnum } from '../../../enums/serviceName';
import useServices from '../../../hooks/useServices';
import { AxiosService } from '../../../utils/axiosService';

export interface NewsFormData {
  titleTh: string;
  titleEn: string;
  descriptionTh: string;
  descriptionEn: string;
  linkUrl: string;
  isPublished: boolean;
  coverImages: MyFile[];
}

interface Props {
  mode: 'create' | 'edit';
  newsId?: string;
  defaultValues?: NewsFormData;
}

const defaultFormValues: NewsFormData = {
  titleTh: '', titleEn: '', descriptionTh: '', descriptionEn: '',
  linkUrl: '', isPublished: false, coverImages: [],
};

const labelStyle: React.CSSProperties = {
  display: 'block', marginBottom: 6, color: 'gray', fontSize: '0.85rem',
};
const errorStyle: React.CSSProperties = {
  display: 'block', marginTop: 4, color: '#ff4d4f', fontSize: '0.8rem',
};

const FormNews: React.FC<Props> = ({ mode, newsId, defaultValues }) => {
  const navigate = useNavigate();
  const { service: createService } = useServices(ServiceNameEnum.news.create);
  const { service: editService } = useServices(ServiceNameEnum.news.edit);

  const { control, handleSubmit, formState: { errors } } = useForm<NewsFormData>({
    defaultValues: defaultValues || defaultFormValues,
  });

  const onSubmit = async (formData: NewsFormData): Promise<void> => {
    const coverFile = formData.coverImages?.[0];
    const payload = {
      titleML: { th: formData.titleTh, en: formData.titleEn },
      descriptionML: { th: formData.descriptionTh, en: formData.descriptionEn },
      coverFileId: coverFile?.id || null,
      linkUrl: formData.linkUrl || null,
      isPublished: formData.isPublished,
    };

    if (mode === 'create') {
      const { data, error } = await createService.post(payload);
      if (data) { toast.success('News created successfully'); navigate(RouteNameEnum.news.index); }
      else if (error) { toast.error(AxiosService.getErrorResponse(error)?.message, { toastId: 'allToast' }); }
    } else {
      const { data, error } = await editService.put(payload, { params: { id: newsId } });
      if (data) { toast.success('News updated successfully'); navigate(RouteNameEnum.news.index); }
      else if (error) { toast.error(AxiosService.getErrorResponse(error)?.message, { toastId: 'allToast' }); }
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} style={{ marginTop: 32 }}>
      {/* Title */}
      <HeaderLabel label="Title" />
      <div style={{ background: 'white', padding: 16 }}>
        <Row gutter={[16, 16]}>
          <Col xs={24} md={12}>
            <span style={labelStyle}>Title (TH)</span>
            <Controller
              name="titleTh"
              control={control}
              rules={{ required: 'Title (TH) is required' }}
              render={({ field, fieldState }) => (
                <>
                  <Input {...field} size="large" status={fieldState.error ? 'error' : ''} />
                  {errors.titleTh && <span style={errorStyle}>{errors.titleTh.message}</span>}
                </>
              )}
            />
          </Col>
          <Col xs={24} md={12}>
            <span style={labelStyle}>Title (EN)</span>
            <Controller
              name="titleEn"
              control={control}
              rules={{ required: 'Title (EN) is required' }}
              render={({ field, fieldState }) => (
                <>
                  <Input {...field} size="large" status={fieldState.error ? 'error' : ''} />
                  {errors.titleEn && <span style={errorStyle}>{errors.titleEn.message}</span>}
                </>
              )}
            />
          </Col>
        </Row>
      </div>

      {/* Cover Image */}
      <div style={{ marginTop: 32 }}>
        <HeaderLabel label="Cover Image" />
        <div style={{ padding: '1rem', background: 'white' }}>
          <div style={{
            display: 'flex', flexWrap: 'wrap', marginTop: 8, flexDirection: 'column',
          }}
          >
            <div style={{ display: 'flex' }}>
              <Controller
                name="coverImages"
                control={control}
                render={({ field: { onChange, value } }) => (
                  <DropZone
                    width={320}
                    height={180}
                    maxFile={1}
                    maxFileSize={1024 * 1024 * 2}
                    onChange={onChange}
                    defaultValue={value}
                    allowFileTypes={[FileType.JPEG, FileType.PNG]}
                    allowDimension={{ width: 1920, height: 1080 }}
                    err={false}
                  />
                )}
              />
            </div>
            <div>
              <ul>
                <li><Typography.Text type="secondary">Required W 1920 x H 1080 px (16:9), max 2 MB</Typography.Text></li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Description (Rich Text) */}
      <div style={{ marginTop: 32 }}>
        <HeaderLabel label="Description" />
        <div style={{ background: 'white', padding: 16 }}>
          <Row gutter={[16, 16]}>
            <Col xs={24} md={12}>
              <span style={labelStyle}>Description (TH)</span>
              <Controller
                name="descriptionTh"
                control={control}
                render={({ field: { onChange, value } }) => <TextEditor value={value} defaultValue={value} onChange={onChange} error={false} />}
              />
            </Col>
            <Col xs={24} md={12}>
              <span style={labelStyle}>Description (EN)</span>
              <Controller
                name="descriptionEn"
                control={control}
                render={({ field: { onChange, value } }) => <TextEditor value={value} defaultValue={value} onChange={onChange} error={false} />}
              />
            </Col>
          </Row>
        </div>
      </div>

      {/* Link URL */}
      <div style={{ marginTop: 32 }}>
        <HeaderLabel label="Link URL (optional)" />
        <div style={{ background: 'white', padding: 16 }}>
          <span style={labelStyle}>Link URL</span>
          <Controller
            name="linkUrl"
            control={control}
            render={({ field }) => <Input {...field} size="large" placeholder="https://..." />}
          />
        </div>
      </div>

      {/* Visibility */}
      <div style={{ marginTop: 32 }}>
        <HeaderLabel label="Visibility" />
        <div style={{ background: 'white', padding: 16 }}>
          <Controller
            name="isPublished"
            control={control}
            render={({ field }) => (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                <Switch checked={field.value} onChange={field.onChange} />
                <span>Published (visible in app)</span>
              </span>
            )}
          />
        </div>
      </div>

      <Divider style={{ marginTop: 32, marginBottom: 32 }} />
      <div style={{ display: 'flex', justifyContent: 'center', gap: 16 }}>
        <AlumlyButton label="rxvWhite" width={128} onClick={() => navigate(RouteNameEnum.news.index)}>Cancel</AlumlyButton>
        <AlumlyButton label="rxv" width={200} type="submit">{mode === 'create' ? 'Create' : 'Save Changes'}</AlumlyButton>
      </div>
    </form>
  );
};

FormNews.defaultProps = { newsId: undefined, defaultValues: undefined };
export default FormNews;
